import { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement,
  BarElement,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { useTimeRange } from "../../../context/TimeRangeContext";
import { formatLabels } from "../../../utils/formatLabels";

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement,
  BarElement
);

const AreaChart = ({ chartType = "line" }) => {
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { timeRange } = useTimeRange();

  const donutColors = [
    "#3B82F6",
    "#6366F1",
    "#10B981",
    "#F59E0B",
    "#EF4444",
    "#8B5CF6",
    "#0EA5E9",
    "#F472B6",
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authorization token not found.");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `https://zola.technology/api/company/surveys/get-data/nps/spread-data/?filter=${timeRange}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP Error: ${response.status}`);
        }

        const data = await response.json();
        const periods = data?.periods || [];
        const labels = formatLabels(periods, timeRange);
        const scores = periods.map((item) => item.nps_score ?? 0);

        if (chartType === "doughnut") {
          setChartData({
            labels,
            datasets: [
              {
                label: "NPS",
                data: scores,
                backgroundColor: labels.map(
                  (_, i) => donutColors[i % donutColors.length]
                ),
                borderWidth: 1,
              },
            ],
          });
        } else {
          setChartData({
            labels,
            datasets: [
              {
                label: "NPS",
                data: scores,
                backgroundColor:
                  chartType === "doughnut"
                    ? [
                        "#3366FF",
                        "#66bb6a",
                        "#ff7043",
                        "#ab47bc",
                        "#ffa726",
                        "#ab47bc",
                      ]
                    : chartType === "bar"
                    ? "#3366FF"
                    : "rgba(51,102,255,0.2)",
                borderColor: "#3B82F6",
                borderWidth: 2,
                tension: 0.4,
                fill: chartType === "area",
                pointRadius: 3,
              },
            ],
          });
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeRange, chartType]);

  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: { color: "#333" },
      },
    },
    scales:
      chartType !== "doughnut"
        ? {
            x: {
              ticks: { color: "#6B7280", font: { size: 12 } },
              grid: { display: false },
            },
            y: {
              ticks: { color: "#6B7280", font: { size: 12 } },
              grid: { color: "#e5e7eb" },
              beginAtZero: true,
            },
          }
        : {},
  };

  return (
    <div className="w-full h-[300px]">
      {loading ? (
        <p>Loading chart data...</p>
      ) : error ? (
        <p className="text-red-500">Error: {error}</p>
      ) : chartType === "bar" ? (
        <Bar data={chartData} options={baseOptions} />
      ) : chartType === "doughnut" ? (
        <Doughnut
          data={chartData}
          options={{
            ...baseOptions,
            cutout: "70%",
            scales: {}, // ✅ override scales for donut to hide cartesian axes
          }}
        />
      ) : (
        <Line data={chartData} options={baseOptions} />
      )}
    </div>
  );
};

export default AreaChart;
